import React, { useCallback, useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import type { MixpanelSignupPromptSource } from "@/constants/mixpanel.constants";
import useMixpanelEvent from "@/hooks/useMixpanelEvent";
import { useUser } from "@/context/UserContext";

interface GuestAuthPromptModalProps {
    show: boolean;
    onClose: () => void;
    onGoogle: () => void;
    onEmail: () => void;
    description: string;
    source: MixpanelSignupPromptSource;
}

const GuestAuthPromptModal: React.FC<GuestAuthPromptModalProps> = ({
    show,
    onClose,
    onGoogle,
    onEmail,
    description,
    source,
}) => {
    const { isAuthenticated } = useAuth0();
    const { userState } = useUser();
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const titleId = "guest-auth-prompt-title";
    const descriptionId = "guest-auth-prompt-description";
    const closeButtonRef = React.useRef<HTMLButtonElement>(null);
    const hasTrackedShownRef = useRef(false);
    const isGuest = !isAuthenticated && !userState?.is_created;

    useEffect(() => {
        if (!show) {
            hasTrackedShownRef.current = false;
            return;
        }
        closeButtonRef.current?.focus();
    }, [show]);

    useEffect(() => {
        if (!show) return;
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };
        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [onClose, show]);

    useEffect(() => {
        if (!show || !isGuest || hasTrackedShownRef.current) {
            return;
        }
        hasTrackedShownRef.current = true;
        sendMixpanelEvent("Signup Prompt Shown", {
            source,
            pageUrl: window.location.href,
            email: userState?.emailAddress ?? null,
            nickname: userState?.nickname ?? null,
        });
    }, [isGuest, show, sendMixpanelEvent, source, userState?.emailAddress, userState?.nickname]);

    const trackCtaClick = useCallback((): void => {
        if (!isGuest) {
            return;
        }
        sendMixpanelEvent("Signup Prompt CTA Clicked", {
            source,
            pageUrl: window.location.href,
            email: userState?.emailAddress ?? null,
            nickname: userState?.nickname ?? null,
        });
    }, [isGuest, sendMixpanelEvent, source, userState?.emailAddress, userState?.nickname]);

    const handleGoogleClick = useCallback((): void => {
        trackCtaClick();
        onGoogle();
    }, [onGoogle, trackCtaClick]);

    const handleEmailClick = useCallback((): void => {
        trackCtaClick();
        onEmail();
    }, [onEmail, trackCtaClick]);

    if (!show) return null;

    return (
        <div
            className="tw-fixed tw-inset-0 tw-z-[1000] tw-flex tw-items-center tw-justify-center tw-p-4 tw-bg-black tw-bg-opacity-50"
            onClick={onClose}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={descriptionId}
                className="tw-bg-[#fff] tw-rounded-2xl tw-p-6 sm:tw-p-5 tw-max-w-sm tw-w-full tw-relative tw-mx-4"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    aria-label="Close sign in modal"
                    ref={closeButtonRef}
                    onClick={onClose}
                    className="tw-bg-[#fff] tw-absolute tw-top-3 tw-right-3 sm:tw-top-4 sm:tw-right-4 tw-text-gray-400 hover:tw-text-gray-600 tw-text-2xl tw-leading-none"
                >
                    ×
                </button>
                <h2
                    id={titleId}
                    className="tw-text-[#000] tw-text-xl sm:tw-text-2xl tw-font-bold tw-mb-2 tw-text-center"
                >
                    Sign in to PriceMe <br /> with Google
                </h2>
                <p
                    id={descriptionId}
                    className="tw-text-[#4A5565] tw-text-sm sm:tw-text-base tw-text-center tw-mb-6"
                >
                    {description}
                </p>
                <button
                    type="button"
                    onClick={handleGoogleClick}
                    className="tw-w-full tw-bg-[#100F13] tw-border tw-border-gray-200 tw-text-[#fff] tw-font-semibold tw-py-2.5 sm:tw-py-3 tw-px-4 sm:tw-px-6 tw-rounded-[18px] tw-transition tw-shadow-md hover:tw-shadow-lg tw-flex tw-items-center tw-justify-center tw-gap-2 sm:tw-gap-3 tw-text-sm sm:tw-text-base"
                >
                    <svg className="tw-w-6 tw-h-6" viewBox="0 0 24 24" fill="currentColor">
                        <path
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            fill="#4285F4"
                        />
                        <path
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            fill="#34A853"
                        />
                        <path
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            fill="#FBBC05"
                        />
                        <path
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            fill="#EA4335"
                        />
                    </svg>
                    Continue with Google
                </button>
                <button
                    type="button"
                    onClick={handleEmailClick}
                    className="tw-mt-4 tw-bg-transparent tw-w-full tw-text-center tw-text-[#3B166A] hover:tw-text-[#9B64DF] tw-font-Regular tw-cursor-pointer"
                >
                    Sign in with email
                </button>
            </div>
        </div>
    );
};

export default GuestAuthPromptModal;
